import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/sessions';

const C = {
  bg:'#0D1B2A', surface:'#112233', surface2:'#162840',
  border:'rgba(255,255,255,.08)', border2:'rgba(255,255,255,.12)',
  text:'#F0F6FF', text2:'#A8BFD0', text3:'#607080',
  gold:'#C9A84C', goldLight:'#F5EDD4',
  accent:'#3AAF58', accentLight:'rgba(58,175,88,.15)',
  red:'#EF4444', redBg:'rgba(239,68,68,.12)',
  amber:'#F59E0B', amberBg:'rgba(245,158,11,.12)',
  blue:'#3B82F6', blueBg:'rgba(59,130,246,.12)',
  purple:'#8B5CF6', purpleBg:'rgba(139,92,246,.12)',
  primary:'#153D4B',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const SENTIMENT_CONFIG = {
  'Very Interested': { color:'#10B981', bg:'rgba(16,185,129,.15)', icon:'🔥' },
  'Interested':      { color:'#3B82F6', bg:'rgba(59,130,246,.15)', icon:'😊' },
  'Negotiating':     { color:'#F59E0B', bg:'rgba(245,158,11,.15)', icon:'🤝' },
  'Price Sensitive': { color:'#F59E0B', bg:'rgba(245,158,11,.15)', icon:'💰' },
  'Cold':            { color:'#6B7280', bg:'rgba(107,114,128,.15)', icon:'❄️' },
  'Comparing':       { color:'#EF4444', bg:'rgba(239,68,68,.15)', icon:'⚖️' },
};

const STAGE_COLORS = {
  Hot:         '#10B981',
  Negotiation: '#F59E0B',
  Qualified:   '#3B82F6',
  Contacted:   '#8B5CF6',
  New:         '#6B7280',
  Cold:        '#EF4444',
  Converted:   '#10B981',
  Lost:        '#EF4444',
};

const ScoreRing = ({ score, size=56 }) => {
  const color = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : score >= 40 ? '#3B82F6' : '#EF4444';
  const r = (size-8)/2;
  const circ = 2*Math.PI*r;
  const dash = (score/100)*circ;
  return (
    <div style={{position:'relative',width:size,height:size,flexShrink:0}}>
      <svg width={size} height={size} style={{transform:'rotate(-90deg)'}}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth={6} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',
        fontSize:size>48?14:11,fontWeight:900,color}}>
        {score}
      </div>
    </div>
  );
};

export default function LeadAI() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [aiAdvice, setAiAdvice] = useState('');
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [msg, setMsg] = useState('');
  const [sortBy, setSortBy] = useState('score');

  const showMsg = m => { setMsg(m); setTimeout(()=>setMsg(''),4000); };

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/ai/leads`);
      const d = await r.json();
      setLeads(Array.isArray(d)?d:[]);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(()=>{ loadLeads(); },[loadLeads]);

  const scoreAllLeads = async () => {
    setScoring(true);
    showMsg('AI is scoring all leads...');
    try {
      const r = await fetch(`${API}/ai/score-leads`, { method:'POST', headers:{'Content-Type':'application/json'} });
      const d = await r.json();
      if (r.ok) { setLeads(d.leads||[]); showMsg(`✅ ${d.scored} leads scored by AI!`); }
      else showMsg('Scoring failed: ' + d.error);
    } catch(e) { showMsg('Error'); }
    setScoring(false);
  };

  const getLeadAdvice = async (lead) => {
    setSelectedLead(lead);
    setLoadingAdvice(true);
    setAiAdvice('');
    try {
      const r = await fetch(`${API}/ai/chat`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          system: `You are Botanix Sales AI — expert in hospitality sales, weddings, and MICE. Give specific, actionable sales advice.`,
          messages: [{
            role: 'user',
            content: `Give me a detailed sales strategy for this lead:
Name: ${lead.name}
Event: ${lead.event_type}
Value: ₹${Number(lead.value).toLocaleString('en-IN')}
Stage: ${lead.stage}
Source: ${lead.source}
AI Score: ${lead.ai_score}/100
Sentiment: ${lead.sentiment}
Conversion Probability: ${lead.conversion_probability}%
Notes: ${lead.notes || 'None'}
Assigned to: ${lead.assigned_to || 'Unassigned'}
Follow-up due: ${lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString('en-IN') : 'Not set'}

Provide:
1. SITUATION ASSESSMENT (2-3 lines)
2. NEXT 3 ACTIONS (specific, with timeline)
3. WHAT TO SAY (exact opening line for the call/message)
4. RISK FACTORS (what could make us lose this)
5. UPSELL OPPORTUNITY (what additional services to propose)`
          }]
        })
      });
      const d = await r.json();
      setAiAdvice(d.content?.[0]?.text || 'No advice generated');
    } catch(e) { setAiAdvice('Error getting AI advice'); }
    setLoadingAdvice(false);
  };

  const updateStage = async (id, stage) => {
    await fetch(`${API}/marketing/leads/${id}`, {
      method:'PUT', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({...leads.find(l=>l.id===id), stage})
    });
    setLeads(p=>p.map(l=>l.id===id?{...l,stage}:l));
    showMsg('Stage updated!');
  };

  const filtered = leads.filter(l => {
    const matchSearch = !search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.phone?.includes(search);
    const matchFilter = filter==='all' || 
      (filter==='hot' && l.ai_score >= 80) ||
      (filter==='warm' && l.ai_score >= 50 && l.ai_score < 80) ||
      (filter==='cold' && l.ai_score < 50) ||
      (filter==='followup' && new Date(l.follow_up_date) <= new Date()) ||
      (filter===l.stage?.toLowerCase());
    return matchSearch && matchFilter;
  }).sort((a,b) => {
    if (sortBy==='score') return (b.ai_score||0)-(a.ai_score||0);
    if (sortBy==='value') return parseFloat(b.value||0)-parseFloat(a.value||0);
    if (sortBy==='date') return new Date(b.created_at)-new Date(a.created_at);
    return 0;
  });

  const hotLeads = leads.filter(l=>l.ai_score>=80).length;
  const totalPipeline = leads.reduce((s,l)=>s+parseFloat(l.value||0),0);
  const avgScore = leads.length ? Math.round(leads.reduce((s,l)=>s+(l.ai_score||0),0)/leads.length) : 0;
  const overdueFollowups = leads.filter(l=>l.follow_up_date&&new Date(l.follow_up_date)<new Date()&&!['Converted','Lost'].includes(l.stage)).length;

  return (
    <div style={{background:C.bg,minHeight:'100vh',color:C.text,padding:'24px 28px',fontFamily:'system-ui,sans-serif'}}>

      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text,display:'flex',alignItems:'center',gap:10}}>
            🎯 Lead Intelligence AI
          </div>
          <div style={{fontSize:13,color:C.text3,marginTop:2}}>AI-powered lead scoring, next best action & conversion intelligence</div>
        </div>
        <button onClick={scoreAllLeads} disabled={scoring}
          style={{padding:'10px 20px',background:scoring?C.surface2:`linear-gradient(135deg,${C.gold},#E8C97A)`,
            color:scoring?C.text3:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:scoring?'default':'pointer'}}>
          {scoring?'⏳ AI Scoring...':'🧠 Score All Leads'}
        </button>
      </div>

      {msg&&<div style={{padding:'10px 16px',borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600,background:C.accentLight,color:C.accent}}>{msg}</div>}

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20}}>
        {[
          {label:'Total Leads',value:leads.length,icon:'📋',color:C.blue},
          {label:'Hot Leads (80+)',value:hotLeads,icon:'🔥',color:'#10B981'},
          {label:'Avg AI Score',value:`${avgScore}/100`,icon:'🧠',color:C.gold},
          {label:'Pipeline Value',value:inr(totalPipeline),icon:'💰',color:C.gold},
          {label:'Overdue Follow-ups',value:overdueFollowups,icon:'⚠️',color:overdueFollowups>0?C.red:C.accent},
        ].map((s,i)=>(
          <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 16px',borderTop:`3px solid ${s.color}`}}>
            <div style={{fontSize:20,marginBottom:6}}>{s.icon}</div>
            <div style={{fontSize:s.value?.toString().length>6?16:22,fontWeight:900,color:s.color}}>{s.value}</div>
            <div style={{fontSize:10,color:C.text3,marginTop:2,textTransform:'uppercase'}}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:selectedLead?'1fr 400px':'1fr',gap:16}}>
        {/* Lead List */}
        <div>
          {/* Filters */}
          <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search leads..."
              style={{padding:'8px 14px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13,width:180,outline:'none'}} />
            {[['all','All'],['hot','🔥 Hot (80+)'],['warm','😊 Warm'],['cold','❄️ Cold'],['followup','⚠️ Overdue']].map(([id,label])=>(
              <button key={id} onClick={()=>setFilter(id)}
                style={{padding:'6px 14px',background:filter===id?C.gold:C.surface,color:filter===id?'#1A1A1A':C.text2,
                  border:`1px solid ${filter===id?C.gold:C.border}`,borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                {label}
              </button>
            ))}
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
              style={{padding:'6px 12px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:12,marginLeft:'auto'}}>
              <option value="score">Sort: AI Score</option>
              <option value="value">Sort: Value</option>
              <option value="date">Sort: Date</option>
            </select>
          </div>

          {loading ? (
            <div style={{textAlign:'center',padding:60,color:C.text3}}>Loading leads...</div>
          ) : filtered.length===0 ? (
            <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
              No leads found. Click "Score All Leads" to run AI scoring.
            </div>
          ) : (
            <div style={{display:'grid',gap:10}}>
              {filtered.map(lead=>{
                const sentiment = SENTIMENT_CONFIG[lead.sentiment] || {color:C.text3,bg:C.surface2,icon:'💬'};
                const isOverdue = lead.follow_up_date && new Date(lead.follow_up_date) < new Date() && !['Converted','Lost'].includes(lead.stage);
                const isSelected = selectedLead?.id===lead.id;
                return (
                  <div key={lead.id}
                    style={{background:isSelected?C.surface2:C.surface,border:`1px solid ${isSelected?C.gold:isOverdue?C.red:C.border}`,
                      borderRadius:12,padding:'16px 18px',cursor:'pointer',transition:'all .2s',
                      borderLeft:`4px solid ${STAGE_COLORS[lead.stage]||C.text3}`}}
                    onClick={()=>lead.ai_score?getLeadAdvice(lead):null}>

                    <div style={{display:'flex',gap:14,alignItems:'flex-start'}}>
                      {/* Score Ring */}
                      <ScoreRing score={lead.ai_score||0} size={56} />

                      {/* Lead Info */}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                          <div>
                            <div style={{fontSize:15,fontWeight:800,color:C.text}}>{lead.name}</div>
                            <div style={{fontSize:12,color:C.text3,marginTop:2}}>
                              {lead.event_type} · {lead.source} · {lead.assigned_to||'Unassigned'}
                            </div>
                          </div>
                          <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                            <div style={{fontSize:16,fontWeight:900,color:C.gold}}>{inr(lead.value)}</div>
                            <div style={{fontSize:11,color:C.text3,marginTop:2}}>{lead.conversion_probability||0}% probability</div>
                          </div>
                        </div>

                        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
                          <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:700,
                            background:`${STAGE_COLORS[lead.stage]||C.text3}22`,color:STAGE_COLORS[lead.stage]||C.text3}}>
                            {lead.stage}
                          </span>
                          {lead.sentiment && (
                            <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:700,background:sentiment.bg,color:sentiment.color}}>
                              {sentiment.icon} {lead.sentiment}
                            </span>
                          )}
                          {isOverdue && (
                            <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:700,background:C.redBg,color:C.red}}>
                              ⚠️ Follow-up Overdue
                            </span>
                          )}
                        </div>

                        {lead.ai_next_action && (
                          <div style={{fontSize:12,color:C.gold,fontWeight:600,marginBottom:8}}>
                            🤖 {lead.ai_next_action}
                          </div>
                        )}

                        {lead.notes && (
                          <div style={{fontSize:11,color:C.text3,fontStyle:'italic',marginBottom:8}}>
                            "{lead.notes.slice(0,100)}{lead.notes.length>100?'...':''}"
                          </div>
                        )}

                        <div style={{display:'flex',gap:8,alignItems:'center'}}>
                          <select value={lead.stage} onChange={e=>{e.stopPropagation();updateStage(lead.id,e.target.value);}}
                            onClick={e=>e.stopPropagation()}
                            style={{padding:'4px 8px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:6,
                              color:STAGE_COLORS[lead.stage]||C.text2,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                            {['New','Contacted','Qualified','Proposal Sent','Negotiation','Hot','Converted','Lost'].map(s=>(
                              <option key={s} style={{color:'#1A1A1A'}}>{s}</option>
                            ))}
                          </select>
                          <button onClick={e=>{e.stopPropagation();window.open(`https://wa.me/91${lead.phone}?text=${encodeURIComponent(`Hi ${lead.name.split(' ')[0]}, this is from Botanix Nature Resort. We'd love to discuss your ${lead.event_type} requirements.`)}`)}}
                            style={{padding:'4px 10px',background:'rgba(37,211,102,.15)',color:'#25D366',border:'1px solid rgba(37,211,102,.3)',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>
                            💬 WhatsApp
                          </button>
                          {lead.phone && (
                            <button onClick={e=>{e.stopPropagation();window.location.href=`tel:${lead.phone}`;}}
                              style={{padding:'4px 10px',background:C.blueBg,color:C.blue,border:`1px solid ${C.blue}33`,borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>
                              📞 Call
                            </button>
                          )}
                          <button onClick={e=>{e.stopPropagation();getLeadAdvice(lead);}}
                            style={{padding:'4px 10px',background:`${C.gold}22`,color:C.gold,border:`1px solid ${C.gold}44`,borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>
                            🧠 AI Advice
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* AI Advice Panel */}
        {selectedLead && (
          <div style={{background:C.surface,border:`1px solid ${C.gold}44`,borderRadius:14,padding:20,height:'fit-content',position:'sticky',top:20}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:800,color:C.gold}}>🧠 AI Sales Strategy</div>
              <button onClick={()=>setSelectedLead(null)}
                style={{background:'transparent',border:'none',color:C.text3,cursor:'pointer',fontSize:16}}>✕</button>
            </div>

            <div style={{marginBottom:14,padding:'12px 14px',background:C.surface2,borderRadius:10}}>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <ScoreRing score={selectedLead.ai_score||0} size={48} />
                <div>
                  <div style={{fontSize:14,fontWeight:800,color:C.text}}>{selectedLead.name}</div>
                  <div style={{fontSize:12,color:C.text3}}>{selectedLead.event_type} · {inr(selectedLead.value)}</div>
                  <div style={{fontSize:11,color:C.gold,fontWeight:600}}>{selectedLead.conversion_probability}% conversion probability</div>
                </div>
              </div>
            </div>

            {loadingAdvice ? (
              <div style={{textAlign:'center',padding:30}}>
                <div style={{fontSize:24,marginBottom:10}}>🧠</div>
                <div style={{fontSize:13,color:C.text3}}>AI analyzing this lead...</div>
                <div style={{display:'flex',justifyContent:'center',gap:4,marginTop:10}}>
                  {[0,1,2].map(i=>(
                    <div key={i} style={{width:6,height:6,borderRadius:'50%',background:C.gold,
                      animation:`pulse ${0.6+i*0.2}s infinite alternate`}} />
                  ))}
                </div>
              </div>
            ) : aiAdvice ? (
              <div style={{fontSize:13,lineHeight:1.8,color:C.text2,whiteSpace:'pre-wrap',maxHeight:400,overflowY:'auto'}}>
                {aiAdvice}
              </div>
            ) : (
              <div style={{textAlign:'center',padding:20,color:C.text3,fontSize:13}}>
                Click "AI Advice" on a lead to get strategy
              </div>
            )}

            {aiAdvice && (
              <div style={{marginTop:14,display:'flex',gap:8}}>
                <button onClick={()=>window.open(`https://wa.me/91${selectedLead.phone}?text=${encodeURIComponent(`Hi ${selectedLead.name.split(' ')[0]}, this is from Botanix Nature Resort regarding your ${selectedLead.event_type} enquiry.`)}`)}
                  style={{flex:1,padding:'8px',background:'rgba(37,211,102,.15)',color:'#25D366',border:'1px solid rgba(37,211,102,.3)',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                  💬 WhatsApp Now
                </button>
                <button onClick={()=>getLeadAdvice(selectedLead)}
                  style={{flex:1,padding:'8px',background:`${C.gold}22`,color:C.gold,border:`1px solid ${C.gold}44`,borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                  ↻ Refresh
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes pulse{from{opacity:.4;transform:scale(.8)}to{opacity:1;transform:scale(1.2)}}`}</style>
    </div>
  );
}
